import {FC, useState} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import Button from 'components/Button';
import DropdownSelect from 'components/Dropdown/DropdownSelect';
import FooterBasic from 'components/Footers/Basic';
import FormHeader from 'components/Form/FormHeader';
import FormResult from 'components/Form/FormResult';
import InputHidden from 'components/Form/InputHidden';
import InputPhoneNumber from 'components/Form/InputPhoneNumber';
import InputPostcode from 'components/Form/InputPostcode';
import InputText from 'components/Form/InputText';
import SubmitButton from 'components/Form/SubmitButton';
import HeaderBasic from 'components/Headers/Basic';
import DualPage from 'components/Layout/DualPage';
import Stacked from 'components/Layout/Stacked';
import {useBackend} from 'state/backend';
import {Backend} from 'types/api';
import {FormFields} from 'types/utils';
import onboardingImage from '../assets/img/onboarding-1.png';
import {PartialAddressType, SetupBackendData} from '../types';
import WizardSectionHeader from '../WizardSectionHeader';

export type AddressDetailsFormData = FormFields<PartialAddressType>;

type AddressDetailsProps = {
    onBack: () => void;
    onSuccess: (data: PartialAddressType) => void;
    prefillData?: PartialAddressType;
    showAlternativeText?: boolean;
    showFax?: boolean;
    showTelephone?: boolean;
};

const AddressDetails: FC<AddressDetailsProps> = ({
    onBack,
    onSuccess,
    prefillData,
    showAlternativeText,
    showFax,
    showTelephone,
}) => {
    const methods = useForm<AddressDetailsFormData>({
        defaultValues: {
            addressFormat: prefillData?.addressFormat || 'jp_default',
            country: prefillData?.country || 'JP',
            extendedAddress: prefillData?.extendedAddress,
            faxNumber: prefillData?.faxNumber,
            locality: prefillData?.locality,
            phoneNumber: prefillData?.phoneNumber,
            postalCode: prefillData?.postalCode,
            region: prefillData?.region,
            streetAddress: prefillData?.streetAddress,
        },
        shouldUnregister: true,
    });
    const {handleSubmit, setValue, watch} = methods;

    const countrySelection = watch('country');

    const isJp = countrySelection === 'JP';

    const {data} = useBackend<Backend<SetupBackendData>>();

    // disable the submit button after a successful submit
    const [success, setSuccess] = useState(false);

    const {t} = useTranslation('Profile.Setup.AddressDetails');

    const onSubmit = async (formData: AddressDetailsFormData) => {
        setSuccess(true);
        const submitData = {...formData};

        if (isJp && submitData.addressFormat === 'default')
            submitData.addressFormat = 'jp_default';
        else if (!isJp) {
            submitData.addressFormat = 'default';
        }

        onSuccess(submitData);
    };

    return (
        <div
            className="relative flex min-h-screen flex-col bg-grey-100 "
            id="address-details"
        >
            <HeaderBasic className="px-8" showSignIn={false} />
            <DualPage
                classNameFirstSection="bg-gradient-to-tr from-green-800 to-green-600"
                classNameInner="overflow-hidden rounded-lg"
                classNameSecondSection="bg-white"
                pagination={{
                    current: 2,
                    length: 3,
                }}
                size="6xl"
            >
                <>
                    <img
                        alt="post-signup-wizard-img"
                        className="invisible absolute bottom-0 w-full md:visible"
                        src={onboardingImage}
                    />
                    <WizardSectionHeader
                        subTitle={
                            showAlternativeText
                                ? t('information.altSubTitle')
                                : t('information.subtitle')
                        }
                        title={
                            showAlternativeText
                                ? t('information.altTitle')
                                : t('information.title')
                        }
                    />
                </>
                <FormProvider {...methods}>
                    <form className="p-6" onSubmit={handleSubmit(onSubmit)}>
                        <FormHeader
                            className="mt-0"
                            hideHR={true}
                            title={t('pageForm.title')}
                        />
                        <Stacked className="mt-4 w-full">
                            <DropdownSelect
                                aria-label="country name"
                                isSearchable={true}
                                label={t('form:country')}
                                name="country"
                                options={data.countries}
                                required={true}
                            />
                            <InputHidden name="addressFormat" />
                            <InputPostcode
                                helpText={t('helpText.postcode')}
                                label={t('form:postcode')}
                                name="postalCode"
                                onAutofill={(autoFillData) => {
                                    setValue(
                                        'region',
                                        isJp
                                            ? autoFillData.region
                                            : autoFillData.pref,
                                    );
                                    setValue('locality', autoFillData.city);
                                    setValue(
                                        'streetAddress',
                                        autoFillData.nbhd,
                                    );
                                }}
                                showAutofill={isJp}
                            />
                            {isJp ? (
                                <DropdownSelect
                                    aria-label="prefecture"
                                    isSearchable={true}
                                    label={t('form:prefecture')}
                                    name="region"
                                    options={data.prefectures}
                                    required={true}
                                />
                            ) : (
                                <InputText
                                    aria-label="prefecture"
                                    label={t('form:prefecture')}
                                    name="region"
                                    required={true}
                                />
                            )}
                            <InputText
                                aria-label="locality"
                                helpText={t('helpText.city')}
                                label={t('form:city')}
                                name="locality"
                                required={true}
                            />
                            <InputText
                                helpText={t('helpText.neighborhood')}
                                label={t('form:neighborhood')}
                                name="streetAddress"
                                required={true}
                            />
                            <InputText
                                helpText={t('helpText.building')}
                                label={t('form:building')}
                                name="extendedAddress"
                            />
                            {showTelephone && (
                                <InputPhoneNumber
                                    helpText={t('helpText.phoneNumber')}
                                    label={t('form:companyPhoneNumber')}
                                    name="phoneNumber"
                                />
                            )}
                            {showFax && (
                                <InputPhoneNumber
                                    helpText={t('helpText.phoneNumber')}
                                    label={t('form:faxNumber')}
                                    name="faxNumber"
                                />
                            )}
                            <FormResult />
                            <div className="flex">
                                <Button kind="tertiary" onClick={onBack}>
                                    {t('form:goBack')}
                                </Button>
                                <div className="flex flex-1 justify-end gap-6">
                                    <SubmitButton disabled={success}>
                                        {t('next')}
                                    </SubmitButton>
                                </div>
                            </div>
                        </Stacked>
                    </form>
                </FormProvider>
            </DualPage>
            <FooterBasic />
        </div>
    );
};

export default AddressDetails;
